import { css, styled } from "@pigment-css/react";
import ReactMarkdown from "react-markdown";
import remarkBreaks from "remark-breaks";
import remarkGfm from "remark-gfm";
import { ResponsiveTable } from "@acto/taskpane/core/components/markdown";

// Very simple markdown for the assistant message (Acto 2.1, Figma 4124-8492):
// correct type sizes, NO bold/italic emphasis (strong/em render as plain body), and the only
// structural styling is unordered + ordered lists. Body/list use rgba(19,19,19,0.9); the lead
// heading is a hair darker (neutral[900]).

const Lead = styled("p")(({ theme }) => ({
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[17],
  fontWeight: theme.fieldSalesTwo.font.weight[540],
  lineHeight: "1.52",
  letterSpacing: theme.fieldSalesTwo.font.letterSpacing.subtle,
  color: theme.fieldSalesTwo.colors.neutral[900],
  margin: 0, // block spacing owned by MessageBody's flex gap
}));

const Text = styled("p")(({ theme }) => ({
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[15],
  fontWeight: theme.fieldSalesTwo.font.weight[520],
  lineHeight: theme.fieldSalesTwo.font.lineHeight[164],
  letterSpacing: theme.fieldSalesTwo.font.letterSpacing.subtle,
  color: "rgba(19,19,19,0.9)",
  wordBreak: "break-word",
  margin: 0, // block spacing owned by MessageBody's flex gap
}));

const listStyles = css(({ theme }) => ({
  margin: 0, // block spacing owned by MessageBody's flex gap
  paddingLeft: theme.fieldSalesTwo.spacing[18],
  listStyleType: "disc",
  "ol&": { listStyleType: "decimal" },
  "& li": {
    margin: `0 0 ${theme.fieldSalesTwo.spacing[12]}`,
    fontFamily: theme.fieldSalesTwo.font.family,
    fontSize: theme.fieldSalesTwo.font.size[15],
    fontWeight: theme.fieldSalesTwo.font.weight[520],
    lineHeight: theme.fieldSalesTwo.font.lineHeight[164],
    letterSpacing: theme.fieldSalesTwo.font.letterSpacing.subtle,
    color: "rgba(19,19,19,0.9)",
  },
  "& li::marker": {
    color: theme.fieldSalesTwo.colors.neutral[600],
  },
  "& li:last-child": { marginBottom: 0 },
  "& li > ul, & li > ol": {
    marginTop: theme.fieldSalesTwo.spacing[8],
    marginBottom: 0,
  },
}));

// Strip emphasis: bold/italic render as plain body text, per the design.
const Plain = (props: React.HTMLAttributes<HTMLElement>) => <span {...props} />;

const Link = styled("a")(({ theme }) => ({
  color: theme.fieldSalesTwo.colors.accent.action,
  textDecoration: "none",
  wordBreak: "break-word",
  overflowWrap: "anywhere",
  "&:hover": { textDecoration: "underline" },
}));

const mdComponents = {
  h1: (props: React.HTMLAttributes<HTMLHeadingElement>) => <Lead as="h1" {...props} />,
  h2: (props: React.HTMLAttributes<HTMLHeadingElement>) => <Lead as="h2" {...props} />,
  h3: (props: React.HTMLAttributes<HTMLHeadingElement>) => <Lead as="h3" {...props} />,
  h4: (props: React.HTMLAttributes<HTMLHeadingElement>) => <Lead as="h4" {...props} />,
  p: (props: React.HTMLAttributes<HTMLParagraphElement>) => <Text {...props} />,
  strong: Plain,
  em: Plain,
  a: ({ href, children, ...props }: React.AnchorHTMLAttributes<HTMLAnchorElement>) => (
    <Link {...props} href={href} target="_blank" rel="noopener noreferrer">
      {children}
    </Link>
  ),
  ul: (props: React.HTMLAttributes<HTMLUListElement>) => <ul className={listStyles} {...props} />,
  ol: (props: React.HTMLAttributes<HTMLOListElement>) => <ol className={listStyles} {...props} />,
  // Responsive, fieldSalesTwo-themed table: stacks to key/value cards on the narrow widget width,
  // so agent tables stay within the message padding instead of overflowing the page.
  table: (props: React.HTMLAttributes<HTMLTableElement>) => <ResponsiveTable {...props} />,
};

interface MarkdownRendererProps {
  content: string;
}

export const MarkdownRenderer = ({ content }: MarkdownRendererProps) => {
  return (
    <ReactMarkdown remarkPlugins={[remarkBreaks, remarkGfm]} components={mdComponents}>
      {content}
    </ReactMarkdown>
  );
};
